/**
 * KOPS-462: Post-Event Survey Page
 * aisearchportal.com/events/:eventId/survey
 */

import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCheckCircle, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A',
  white: '#FFFFFF', muted: '#8FA3BF'
};

const SurveyPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email') || '';
  const prefilledName = searchParams.get('name') || '';

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [mostValuable, setMostValuable] = useState('');
  const [wantNext, setWantNext] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(0);
  const [openFeedback, setOpenFeedback] = useState('');
  const [testimonialConsent, setTestimonialConsent] = useState(false);
  const [email, setEmail] = useState(prefilledEmail);
  const [name, setName] = useState(prefilledName);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating || !email) return;
    setSubmitting(true);
    setError('');

    try {
      await axiosInstance.post(`/events/${eventId}/survey-response`, {
        email, name, overallRating: rating, mostValuable, wantNext, wouldRecommend, openFeedback, testimonialConsent
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <div className="text-center" style={{ maxWidth: '500px', padding: '40px' }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '64px', color: colors.gold2, marginBottom: '20px' }} />
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.white, marginBottom: '12px' }}>
            Thank <span style={{ color: colors.gold2 }}>You!</span>
          </h2>
          <p style={{ color: colors.muted, fontSize: '16px' }}>Your feedback helps us make every session better.</p>
          <Button href="/events" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, marginTop: '20px' }}>
            View Upcoming Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: colors.navy2, borderBottom: `2px solid ${colors.gold}`, padding: '24px 0', textAlign: 'center' }}>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: colors.white }}>
          AI<span style={{ color: colors.gold2 }}>Search</span>Portal
        </span>
      </div>

      <Container style={{ maxWidth: '600px', padding: '40px 20px' }}>
        <div className="text-center mb-4">
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', color: colors.white, letterSpacing: '2px' }}>
            How Was Your <span style={{ color: colors.gold2 }}>Experience?</span>
          </h1>
          <p style={{ color: colors.muted }}>Takes less than 2 minutes. Your feedback shapes future sessions.</p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Email/Name (if not prefilled) */}
          {!prefilledEmail && (
            <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Email *</Form.Label>
                    <Form.Control required type="email" value={email} onChange={e => setEmail(e.target.value)}
                      style={{ background: colors.navy3, color: colors.white, border: 'none' }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Name</Form.Label>
                    <Form.Control value={name} onChange={e => setName(e.target.value)}
                      style={{ background: colors.navy3, color: colors.white, border: 'none' }} />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {/* Overall Rating */}
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '24px', marginBottom: '20px', textAlign: 'center' }}>
            <p style={{ color: colors.white, fontWeight: 600, fontSize: '16px', marginBottom: '16px' }}>Overall, how would you rate this session?</p>
            <div className="d-flex justify-content-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <FontAwesomeIcon
                  key={star} icon={faStar}
                  style={{
                    fontSize: '36px', cursor: 'pointer', transition: 'color 0.2s',
                    color: star <= (hoverRating || rating) ? colors.gold2 : colors.navy3
                  }}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                />
              ))}
            </div>
            <p style={{ color: colors.muted, fontSize: '12px', marginTop: '8px' }}>
              {rating === 1 ? 'Poor' : rating === 2 ? 'Fair' : rating === 3 ? 'Good' : rating === 4 ? 'Very Good' : rating === 5 ? 'Excellent' : 'Click to rate'}
            </p>
          </div>

          {/* Most Valuable */}
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <Form.Group>
              <Form.Label style={{ color: colors.white, fontSize: '14px', fontWeight: 600 }}>What was the most valuable part of the session?</Form.Label>
              <Form.Control as="textarea" rows={3} value={mostValuable} onChange={e => setMostValuable(e.target.value)}
                placeholder="e.g., the prompt framework, virtual staging demo, Fair Housing tips..."
                style={{ background: colors.navy3, color: colors.white, border: 'none', fontSize: '14px' }} />
            </Form.Group>
          </div>

          {/* What Next */}
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <Form.Group>
              <Form.Label style={{ color: colors.white, fontSize: '14px', fontWeight: 600 }}>What topic would you like to learn more about?</Form.Label>
              <Form.Control as="textarea" rows={2} value={wantNext} onChange={e => setWantNext(e.target.value)}
                placeholder="e.g., advanced prompting, AI for social media, virtual staging, seller CMAs..."
                style={{ background: colors.navy3, color: colors.white, border: 'none', fontSize: '14px' }} />
            </Form.Group>
          </div>

          {/* NPS */}
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '24px', marginBottom: '20px', textAlign: 'center' }}>
            <p style={{ color: colors.white, fontWeight: 600, fontSize: '14px', marginBottom: '16px' }}>How likely are you to recommend this to a colleague? (1-10)</p>
            <div className="d-flex justify-content-center gap-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <button key={n} type="button" onClick={() => setWouldRecommend(n)}
                  style={{
                    width: '36px', height: '36px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
                    background: wouldRecommend === n ? colors.gold : colors.navy3,
                    color: wouldRecommend === n ? colors.navy : colors.muted
                  }}>
                  {n}
                </button>
              ))}
            </div>
            <div className="d-flex justify-content-between mt-2" style={{ maxWidth: '360px', margin: '0 auto' }}>
              <span style={{ color: colors.muted, fontSize: '10px' }}>Not likely</span>
              <span style={{ color: colors.muted, fontSize: '10px' }}>Very likely</span>
            </div>
          </div>

          {/* Open Feedback */}
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <Form.Group>
              <Form.Label style={{ color: colors.white, fontSize: '14px', fontWeight: 600 }}>Anything else you'd like to share?</Form.Label>
              <Form.Control as="textarea" rows={3} value={openFeedback} onChange={e => setOpenFeedback(e.target.value)}
                placeholder="Your honest feedback helps us improve..."
                style={{ background: colors.navy3, color: colors.white, border: 'none', fontSize: '14px' }} />
            </Form.Group>
          </div>

          {/* Testimonial Consent */}
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}
            onClick={() => setTestimonialConsent(!testimonialConsent)}>
            <div style={{
              width: '22px', height: '22px', minWidth: '22px', borderRadius: '4px', marginTop: '2px',
              background: testimonialConsent ? colors.gold : 'transparent',
              border: `2px solid ${testimonialConsent ? colors.gold : colors.muted}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
            }}>
              {testimonialConsent && <span style={{ color: colors.navy, fontSize: '14px', fontWeight: 800 }}>✓</span>}
            </div>
            <span style={{ color: colors.muted, fontSize: '13px', lineHeight: '1.5' }}>
              I give KairosIQ AI, Inc. permission to use my feedback as a testimonial on their website and marketing materials. My first name and brokerage may be displayed.
            </span>
          </div>

          <Button type="submit" disabled={submitting || !rating || !email} className="w-100" size="lg"
            style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, fontSize: '16px' }}>
            {submitting ? <><Spinner size="sm" className="me-1" />Submitting...</> : <>Submit Feedback <FontAwesomeIcon icon={faArrowRight} className="ms-2" /></>}
          </Button>
        </Form>
      </Container>
    </div>
  );
};

export default SurveyPage;
